// Assignee/owner faces for a dense table cell.
//
// Why faces instead of names: a name list in a table cell is unbounded. A
// joined "Name, Name, Name" string in a whitespace-nowrap cell is exactly what
// stretched the Tasks grid wider than the viewport and pushed four columns
// off-screen behind the sticky last column. Two 24px circles cost the same
// width whether a row has two people on it or nine.
//
// Hovering one circle names that person; hovering "+N" lists the rest.

// Deterministic colour per person — hashed from the id, never the row index,
// or people would change colour as the grid filters and pages.
const AVATAR_COLORS = ["#0176D3", "#059669", "#D97706", "#7C3AED", "#0891B2", "#DC2626", "#014486"];

export const colorFor = (id = "") => {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// "Amaaun Keshwani" -> "AK". One letter for a single-word name or an email,
// "?" for nothing usable.
export const initialsOf = (name = "") =>
  String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

/**
 * people: [{ id, name, avatar_url? }]
 * limit:  faces shown before the rest collapse into "+N"
 * empty:  rendered when there is nobody
 */
export default function AvatarStack({ people = [], limit = 2, empty = "Unassigned" }) {
  if (people.length === 0) return <span className="text-xs text-zinc-400">{empty}</span>;

  const shown = people.slice(0, limit);
  const rest = people.slice(limit);

  return (
    <div className="flex -space-x-1.5">
      {shown.map((p, i) => (
        <span
          key={p.id ?? i}
          title={p.name}
          className="w-6 h-6 rounded-full ring-2 ring-white flex items-center justify-center overflow-hidden text-[9px] font-semibold text-white cursor-default shrink-0"
          style={{ backgroundColor: colorFor(p.id ?? p.name) }}
        >
          {p.avatar_url
            ? <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" />
            : initialsOf(p.name)}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          title={rest.map((p) => p.name).join(", ")}
          className="w-6 h-6 rounded-full ring-2 ring-white bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[9px] font-semibold text-zinc-600 cursor-default shrink-0"
        >
          +{rest.length}
        </span>
      )}
    </div>
  );
}
